import {type ReactElement, type ReactNode, useEffect, useMemo, useRef} from 'react';
import type {AiChatBodyProps} from './ai-chat-body.types';
import styles from './ai-chat-body.module.scss';
import {AiChatWindow} from '@Components/ai-chat-window/ai-chat-window';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {Text, TextSize} from '@Components/text';
import type {SetChatIdNavigationOptions} from '@Components/ai-chat-window/ai-chat-window.types';
import {ChatMode} from '@Components/ai-chat-window/ai-chat-window.types';
import {AiChatHotjarFeedbackSurvey} from '@/ai-chat/components/ai-chat-hotjar-feedback-survey/ai-chat-hotjar-feedback-survey';
import {useAiChatWindowTitle} from '@/ai-chat/hooks/useAiChatWindowTitle';
import {isUserPremium, isUserPremiumPlus} from '@Libraries/user.library';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {AiChatLandingHero} from '@Components/ai-chat-landing-hero/ai-chat-landing-hero';
import {AiChatLandingHeroSize} from '@Components/ai-chat-landing-hero/ai-chat-landing-hero.types';
import {AiChatQuickTips} from '@AiChat/components/ai-chat-quick-tips/ai-chat-quick-tips';
import {AiChatAssetsView} from '@AiChat/components/ai-chat-assets-view/ai-chat-assets-view';
import {AiChatMemoryView} from '@AiChat/components/ai-chat-memory-view/ai-chat-memory-view';

export function AiChatBody({...props}: AiChatBodyProps): ReactElement {
  const {state, actions} = useAiChat();
  const {isSmall} = useAiChatSize();
  const id = state.promptBoxId;
  const chatWindowRef = useRef<HTMLDivElement>(null);

  useAiChatWindowTitle({
    currentChatId: state.currentChatId,
    chatHistory: state.chatHistory,
    isQuickTipsView: state.isQuickTipsView,
  });

  useEffect(() => {
    if (!chatWindowRef.current) {
      return;
    }

    if (!isSmall || state.isPromptBoxOnly) {
      makePromptBoxActive();
    }
  }, []);

  const getDesktopEmptyStateTitle = (): ReactNode => {
    return (
      <div className={`${styles.titleContainer} flex-column-align-center ${state.hasMessages ? styles.chatHasMessages : ''}`}>
        <div className={styles.titleLine}>
          <Text className={`${styles.title} _unmargin`} val={window.i18next.t('pmwjs_create_with_ai')} />
          <span className={styles.titleIcon}>
            <Icon icon="icon-sparkle-filled" className={styles.titleSparkle} size={IconSize.SIZE_ICON_32} />
          </span>
        </div>
        <Text className={styles.subheading} size={TextSize.SMALL} val={window.i18next.t('pmwjs_customize_templates_write_text')} />
      </div>
    );
  };

  const makePromptBoxActive = (): void => {
    actions.activatePromptBox();
  };

  const onMobileTitleLogoClick = (): void => {
    makePromptBoxActive();
  };

  const getMobileEmptyStateTitle = (): ReactNode => {
    return (
      <div className={`${styles.titleContainer} ${styles.mobileTitleContainer} flex-column-align-center ${state.hasMessages ? styles.chatHasMessages : ''}`}>
        <div className="flex-v-row flex-center gap-1">
          <ClickableDiv className={styles.mobileIconContainer} onClick={onMobileTitleLogoClick}>
            <Icon icon="icon-sparkle-filled" className={`${styles.mobileTitleIcon} content-body-white`} size={IconSize.SIZE_ICON_24} />
          </ClickableDiv>
          <Text className={styles.mobileTitle} val={window.i18next.t('pmwjs_create_with_ai')} />
        </div>
      </div>
    );
  };

  const isReturningUser = state.isReturningUser;

  const getEmptyStateTitle = (): ReactNode => {
    if (state.pendingChatOnLoadParams?.sendOnLoad || state.isPromptBoxOnly) {
      return null;
    }

    if (isReturningUser) {
      return (
        <div className={`${styles.titleContainer} ${state.hasMessages ? styles.chatHasMessages : ''}`}>
          {isSmall ? (
            <ClickableDiv className={styles.mobileIconContainer} onClick={onMobileTitleLogoClick}>
              <Icon icon="icon-sparkle-filled" className={`${styles.mobileTitleIcon} content-body-white`} size={IconSize.SIZE_ICON_24} />
            </ClickableDiv>
          ) : null}
          <AiChatLandingHero size={isSmall ? AiChatLandingHeroSize.SMALL : AiChatLandingHeroSize.LARGE} refreshKey={state.landingTipRefreshKey} />
        </div>
      );
    }

    if (isSmall) {
      return getMobileEmptyStateTitle();
    }

    return getDesktopEmptyStateTitle();
  };

  function refreshHistory(): void {
    actions.setSearchQuery(undefined);

    void actions.refetchChatHistory();
  }

  function handleChatIdChange(newChatId: string | undefined, options?: SetChatIdNavigationOptions): void {
    actions.setCurrentChatId(newChatId, options);
  }

  const getBodyClassNames = (): string => {
    const showsReturningUserEmptyState = !state.hasMessages && isReturningUser && !state.isPromptBoxOnly;

    return [
      styles.aiChatBody,
      isSmall ? styles.aiChatBodySmall : '',
      state.hasMessages ? styles.chatHasMessages : '',
      showsReturningUserEmptyState ? styles.returningUserEmpty : '',
      showsReturningUserEmptyState && isSmall ? styles.returningUserMobile : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  const isPremiumUser = isUserPremium() || isUserPremiumPlus();

  // Keeping this in case needed in the future
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useMemo(() => {
    if (!isPremiumUser && !state.isPaygUserWithAiChatAccess) {
      return null;
    }

    return <AiChatHotjarFeedbackSurvey containerRef={chatWindowRef} />;
  }, [isPremiumUser, state.isPaygUserWithAiChatAccess]);

  const getChatView = (): ReactNode => {
    return (
      <div ref={chatWindowRef} className={`_full-width ${styles.chatWindowContainer}`}>
        {getEmptyStateTitle()}
        <AiChatWindow
          hasFloatingPromptBox={state.hasMessages}
          credits={state.credits}
          refreshCredits={actions.refreshCredits}
          onLoginSuccessActions={actions.onLoginSuccessActions}
          isUserLoggedIn={state.isUserLoggedIn}
          currentlyOpenedChatId={state.currentChatId}
          id={id}
          setCurrentChatId={handleChatIdChange}
          refreshChatHistory={refreshHistory}
          chatOnLoadParams={state.pendingChatOnLoadParams ?? props.chatOnLoadParams}
          onChatOnLoadConsumed={actions.clearPendingChatOnLoadParams}
          mode={state.mode}
          surfaceBias={props.surfaceBias}
          modeSpecificProps={{
            mode: ChatMode.CREATE,
          }}
          userHasChatHistory={isReturningUser}
          onNewChat={(): void => {
            actions.setIsPaygUserWithAiChatAccess(true);
          }}
        />
      </div>
    );
  };

  const getView = (): ReactNode => {
    if (state.isQuickTipsView) {
      return <AiChatQuickTips />;
    }

    if (state.isAssetsView) {
      return <AiChatAssetsView />;
    }

    if (state.isMemoryView) {
      return <AiChatMemoryView />;
    }

    return getChatView();
  };

  return <div className={getBodyClassNames()}>{getView()}</div>;
}
